/**
 * Top context bar (navy): menu toggle, context chips (Tenant / Organization / Role),
 * and the user menu. Mirrors the OneInvoice "PARTNER / TENANT / ORGANIZATION" strip.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from './moduleRegistry';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { List, Globe } from '@phosphor-icons/react';
import { InstallAppButton } from '../pwa/InstallApp';
import UserMenu from './UserMenu';
import { BRAND_NAME } from '../../brand';

function ContextChip({ label, value, testid }) {
  if (!value) return null;
  return (
    <div
      className="relative h-9 min-w-[120px] max-w-[220px] px-3 rounded-md border border-white/40 bg-white/5 flex items-center"
      data-testid={testid}
    >
      <span className="absolute -top-[7px] left-2 px-1 bg-shell-topbar text-[9px] font-semibold tracking-[0.12em] uppercase text-white/80">
        {label}
      </span>
      <span className="text-sm font-semibold text-white truncate">{value}</span>
    </div>
  );
}

export default function TopBar({ onToggleSidebar }) {
  const { user, logout, tenantId, tenantInfo } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    const tenantSubdomain = localStorage.getItem('lms_tenant_subdomain') || tenantInfo?.subdomain;
    const userRole = user?.role;
    const userTenantId = user?.tenant_id || tenantId;
    let redirectUrl = '/login';
    if (userRole !== 'super_admin' && userTenantId && tenantSubdomain) {
      redirectUrl = `/tenant/${tenantSubdomain}/login`;
    }
    logout();
    window.location.href = redirectUrl;
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const isSuper = user?.role === 'super_admin';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';

  return (
    <header className="shell-topbar h-14 flex items-center gap-3 px-3 bg-shell-topbar text-white" data-testid="shell-topbar">
      <button
        type="button"
        onClick={onToggleSidebar}
        className="w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center"
        title="Toggle menu"
        data-testid="topbar-menu-toggle"
      >
        <List className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-3 flex-wrap">
        {isSuper ? (
          <>
            <ContextChip label="Platform" value={BRAND_NAME} testid="ctx-platform" />
            <ContextChip label="Scope" value="All tenants" testid="ctx-scope" />
          </>
        ) : (
          <>
            <ContextChip label="Tenant" value={tenantInfo?.subdomain?.toUpperCase()} testid="ctx-tenant" />
            <ContextChip label="Organization" value={tenantInfo?.name} testid="ctx-organization" />
          </>
        )}
        <ContextChip label="Role" value={ROLE_LABELS[user?.role]} testid="ctx-role" />
      </div>

      <div className="ml-auto flex items-center gap-3">
        {/* Tenant users live in this thing all day — offer them the installed copy.
            Renders nothing once installed, or where the browser cannot install. */}
        {!isSuper && <InstallAppButton />}
        <div className="hidden md:flex items-center gap-1.5 text-xs text-white/80" title="Current host">
          <Globe className="w-4 h-4" />
          <span className="font-mono">{isSuper ? host : (tenantInfo?.custom_domain || tenantInfo?.host || host)}</span>
        </div>
        <UserMenu tone="dark" />
      </div>
    </header>
  );
}
